import React, { useState } from 'react';
import PropTypes from 'prop-types';
import Icons from '../../svgs/icons';

const PlanForm = ({
  plan,
  index,
  onChange,
  onRemove,
  disabled = false,
  isExpanded = false,
  onToggleExpand
}) => {
  const [localErrors, setLocalErrors] = useState({});

  const handleInputChange = (field, value) => {
    onChange(index, field, value);
    // Clear error when user starts typing
    if (localErrors[field]) {
      setLocalErrors((prev) => ({ ...prev, [field]: '' }));
    }
  };

  const handlePointChange = (pointIndex, value) => {
    const updatedPoints = [...(plan.points || [])];
    updatedPoints[pointIndex] = value;
    handleInputChange('points', updatedPoints);
  };

  const handleAddPoint = () => {
    const updatedPoints = [...(plan.points || []), ''];
    handleInputChange('points', updatedPoints);
  };

  const handleRemovePoint = (pointIndex) => {
    const updatedPoints = plan.points?.filter((_, i) => i !== pointIndex) || [];
    handleInputChange('points', updatedPoints);
  };

  const validatePlan = () => {
    const errors = {};

    if (!plan.planName?.trim()) {
      errors.planName = 'Plan name is required';
    }

    if (!plan.description?.trim()) {
      errors.description = 'Description is required';
    }

    setLocalErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleFormBlur = () => {
    if (!disabled) {
      validatePlan();
    }
  };

  if (!isExpanded && !disabled) {
    // Collapsed view for editing
    return (
      <div className="bg-white dark:bg-shoorah-darkBgTabColor border border-gray-300 dark:border-gray-600 rounded-2xl p-4">
        <div className="flex justify-between items-center">
          <div className="flex-1">
            <h4 className="font-medium text-gray-900 dark:text-white">
              {plan.planName || 'New Plan'}
            </h4>
            {plan.description && (
              <p className="text-sm text-gray-600 dark:text-gray-400 mt-1 line-clamp-2">
                {plan.description}
              </p>
            )}
          </div>
          <div className="flex items-center gap-2 ml-4">
            <button
              onClick={() => onToggleExpand(index)}
              className="p-2 text-shoorah-primary hover:text-shoorah-secondary transition-colors"
              title="Edit plan"
            >
              <Icons.edit className="w-5 h-5" />
            </button>
            <button
              onClick={() => onRemove(index)}
              className="p-2 text-red-500 hover:text-red-700 transition-colors"
              title="Remove plan"
            >
              <Icons.trash className="w-5 h-5" />
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (disabled) {
    // Read-only view
    return (
      <div className="bg-white dark:bg-shoorah-darkBgTabColor border border-gray-300 dark:border-gray-600 rounded-2xl p-6">
        <h4 className="font-semibold text-lg text-gray-900 dark:text-white mb-3">
          {plan.planName}
        </h4>

        {plan.description && (
          <p className="text-gray-700 dark:text-gray-300 mb-4">{plan.description}</p>
        )}

        {plan.points && plan.points.length > 0 && plan.points.some((point) => point?.trim()) && (
          <div className="mb-4">
            <h5 className="font-medium text-gray-900 dark:text-white mb-2">Key Points:</h5>
            <ul className="list-disc list-inside space-y-1">
              {plan.points.map(
                (point, idx) =>
                  point?.trim() && (
                    <li key={idx} className="text-gray-700 dark:text-gray-300">
                      {point}
                    </li>
                  )
              )}
            </ul>
          </div>
        )}

        {(plan.contact || plan.info) && (
          <div className="border-t pt-4 mt-4">
            {plan.contact && (
              <p className="text-sm text-gray-700 dark:text-gray-300 mb-2">
                <span className="font-medium">Contact:</span> {plan.contact}
              </p>
            )}
            {plan.info && (
              <p className="text-sm text-gray-700 dark:text-gray-300">
                <span className="font-medium">Info:</span> {plan.info}
              </p>
            )}
          </div>
        )}
      </div>
    );
  }

  // Expanded edit form
  return (
    <div className="bg-white dark:bg-shoorah-darkBgTabColor border border-gray-300 dark:border-gray-600 rounded-2xl p-6 space-y-4">
      {/* Header with collapse and remove buttons */}
      <div className="flex justify-between items-center mb-4">
        <h4 className="font-medium text-gray-900 dark:text-white">
          {plan.planName ? `Edit: ${plan.planName}` : 'New Plan'}
        </h4>
        <div className="flex items-center gap-2">
          <button
            onClick={() => onToggleExpand(index)}
            className="p-2 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 transition-colors"
            title="Collapse"
          >
            <Icons.chevronUp className="w-5 h-5" />
          </button>
          <button
            onClick={() => onRemove(index)}
            className="p-2 text-red-500 hover:text-red-700 transition-colors"
            title="Remove plan"
          >
            <Icons.trash className="w-5 h-5" />
          </button>
        </div>
      </div>

      {/* Plan Name */}
      <div>
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
          Plan Name <span className="text-red-500">*</span>
        </label>
        <input
          type="text"
          className="w-full rounded-lg px-4 py-2 bg-shoorah-normanGray border-[1.5px] border-shoorah-borderGray dark:bg-shoorah-darkBgColor dark:border-shoorah-darkBorder focus:outline-none focus:ring-2 focus:ring-shoorah-primary"
          placeholder="e.g., Basic Health Cash"
          value={plan.planName || ''}
          onChange={(e) => handleInputChange('planName', e.target.value)}
          onBlur={handleFormBlur}
        />
        {localErrors.planName && (
          <p className="text-red-500 text-sm mt-1">{localErrors.planName}</p>
        )}
      </div>

      {/* Description */}
      <div>
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
          Description <span className="text-red-500">*</span>
        </label>
        <textarea
          className="w-full rounded-lg px-4 py-2 bg-shoorah-normanGray border-[1.5px] border-shoorah-borderGray dark:bg-shoorah-darkBgColor dark:border-shoorah-darkBorder focus:outline-none focus:ring-2 focus:ring-shoorah-primary resize-none"
          placeholder="Enter plan description"
          rows="3"
          value={plan.description || ''}
          onChange={(e) => handleInputChange('description', e.target.value)}
          onBlur={handleFormBlur}
        />
        {localErrors.description && (
          <p className="text-red-500 text-sm mt-1">{localErrors.description}</p>
        )}
      </div>

      {/* Key Points */}
      <div>
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
          Key Points
        </label>
        <div className="space-y-2">
          {plan.points?.map((point, pointIndex) => (
            <div key={pointIndex} className="flex gap-2">
              <input
                type="text"
                className="flex-1 rounded-lg px-4 py-2 bg-shoorah-normanGray border-[1.5px] border-shoorah-borderGray dark:bg-shoorah-darkBgColor dark:border-shoorah-darkBorder focus:outline-none focus:ring-2 focus:ring-shoorah-primary"
                placeholder="Enter key point"
                value={point || ''}
                onChange={(e) => handlePointChange(pointIndex, e.target.value)}
              />
              <button
                onClick={() => handleRemovePoint(pointIndex)}
                className="p-2 text-red-500 hover:text-red-700 transition-colors"
                title="Remove point"
              >
                <Icons.trash className="w-5 h-5" />
              </button>
            </div>
          )) || []}

          <button
            onClick={handleAddPoint}
            className="flex items-center gap-2 text-shoorah-primary hover:text-shoorah-secondary text-sm font-medium mt-2"
          >
            <Icons.plusCircle className="w-4 h-4" />
            Add Key Point
          </button>
        </div>
      </div>

      {/* Contact and Info */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Contact
          </label>
          <input
            type="text"
            className="w-full rounded-lg px-4 py-2 bg-shoorah-normanGray border-[1.5px] border-shoorah-borderGray dark:bg-shoorah-darkBgColor dark:border-shoorah-darkBorder focus:outline-none focus:ring-2 focus:ring-shoorah-primary"
            placeholder="Contact information"
            value={plan.contact || ''}
            onChange={(e) => handleInputChange('contact', e.target.value)}
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Additional Info
          </label>
          <input
            type="text"
            className="w-full rounded-lg px-4 py-2 bg-shoorah-normanGray border-[1.5px] border-shoorah-borderGray dark:bg-shoorah-darkBgColor dark:border-shoorah-darkBorder focus:outline-none focus:ring-2 focus:ring-shoorah-primary"
            placeholder="Additional information"
            value={plan.info || ''}
            onChange={(e) => handleInputChange('info', e.target.value)}
          />
        </div>
      </div>
    </div>
  );
};

PlanForm.propTypes = {
  plan: PropTypes.shape({
    planName: PropTypes.string,
    description: PropTypes.string,
    points: PropTypes.arrayOf(PropTypes.string),
    contact: PropTypes.string,
    info: PropTypes.string
  }).isRequired,
  index: PropTypes.number.isRequired,
  onChange: PropTypes.func.isRequired,
  onRemove: PropTypes.func.isRequired,
  disabled: PropTypes.bool,
  isExpanded: PropTypes.bool,
  onToggleExpand: PropTypes.func
};

PlanForm.defaultProps = {
  disabled: false,
  isExpanded: false,
  onToggleExpand: () => {}
};

export default PlanForm;
